<template>
  <div
    class="form-control rich-editor"
    :class="{
      [formControlClass]: formControlClass,
      'is-invalid': state === false,
      'is-valid': state === true,
      focus: focused,
    }"
  >
    <editor-menu-bubble v-if="!readonly" v-slot="{ commands, menu }" :editor="editor">
      <div
        class="bubble-menu"
        :class="{ 'is-active': menu.isActive }"
        :style="`left: ${menu.left}px; bottom: ${menu.bottom}px; transform: translateX(-50%);`"
      >
        <btn
          :class="{ 'is-active': editor.isActive.bold() }"
          icon-name="fa-bold"
          variant="link"
          size="xs"
          icon
          @click.prevent.stop="commands.bold"
        >
          <span class="sr-only">B</span>
        </btn>
        <btn
          :class="{ 'is-active': editor.isActive.italic() }"
          icon-name="fa-italic"
          variant="link"
          size="xs"
          icon
          @click.prevent.stop="commands.italic"
        >
          <span class="sr-only">I</span>
        </btn>
        <btn
          :class="{ 'is-active': editor.isActive.link() }"
          icon-name="fa-link"
          variant="link"
          size="xs"
          icon
          @click.prevent.stop="setLink(editor.activeMarkAttrs.link.href, commands.link)"
        >
          <span class="sr-only">L</span>
        </btn>
      </div>
    </editor-menu-bubble>
    <editor-content
      v-if="editor"
      :editor="editor"
      :style="{
        '--min-lines': minLines || 1,
        '--max-lines': maxGrow,
      }"
      data-cy="rich-editor"
    />
    <rich-suggestion-mention v-if="enableMentions" ref="mention" :editor="editor" :related-case="relatedCase" />
    <rich-suggestion-tag v-if="enableTags" ref="tag" :editor="editor" :related-case="relatedCase" />
    <rich-suggestion-image
      v-if="enableImages"
      ref="image"
      :related-case="relatedCase"
      :editor="editor"
      :images="images"
    />
    <rich-suggestion-group v-if="enableGroups" ref="group" :editor="editor" />
  </div>
</template>

<script>
  import { HardBreak, Placeholder, Link, Italic, Bold, History } from 'tiptap-extensions';
  import RichSuggestionMention from '@/Molecules/RichSuggestionMention';
  import RichSuggestionImage from '@/Molecules/RichSuggestionImage';
  import RichSuggestionGroup from '@/Molecules/RichSuggestionGroup';
  import { Editor, EditorContent, EditorMenuBubble } from 'tiptap';
  import RichSuggestionTag from '@/Molecules/RichSuggestionTag';
  import { formValidationMixin } from '@/validators';
  import { CaseResource } from '@/api-resources';
  import { htmlToText } from '@/filters';
  import debounce from 'lodash.debounce';

  export default {
    name: 'RichEditor',

    mixins: [formValidationMixin],

    components: {
      RichSuggestionMention,
      RichSuggestionGroup,
      RichSuggestionImage,
      RichSuggestionTag,
      EditorMenuBubble,
      EditorContent,
    },

    props: {
      enableMentions: { type: Boolean, default: true },
      enableImages: { type: Boolean, default: true },
      extensions: { type: Array, default: () => [] },
      enableTags: { type: Boolean, default: true },
      relatedCase: CaseResource,
      formControlClass: String,
      enableGroups: Boolean,
      placeholder: String,
      validation: Object,
      readonly: Boolean,
      maxLines: Number,
      output: String, // html, json, text
      images: Array,
      lines: Number,
      value: null,
    },

    data: () => ({
      editor: null,
      focused: false,
      isContentSet: false,
    }),

    computed: {
      minLines() {
        return Math.max(1, this.lines + 0);
      },

      maxGrow() {
        return this.maxLines + 0 || this.minLines;
      },

      defaultExtensions() {
        return [
          new HardBreak(),
          new Placeholder({
            emptyEditorClass: 'editor-empty',
            showOnlyCurrent: false,
            emptyNodeClass: 'editor-placeholder',
            emptyNodeText: () => {
              return this.placeholder;
            },
          }),
          new Link(),
          new Italic(),
          new Bold(),
          new History(),
        ];
      },

      outputType() {
        return this.output || 'html';
      },

      state() {
        if (this.validation) {
          return this.inputState(this.validation);
        }

        return null;
      },
    },

    watch: {
      value() {
        this.setContent();
      },

      editor() {
        this.setContent();
      },
    },

    mounted() {
      this.createEditor();
    },

    methods: {
      setLink(previousUrl, setLink) {
        const url = window.prompt('URL', previousUrl);

        // cancelled
        if (url === null) {
          return;
        }

        // empty
        if (url === '') {
          setLink({});

          return;
        }

        // update link
        setLink({ href: url, target: '_blank' });
      },

      createEditor() {
        const extensions = [...this.defaultExtensions, ...this.extensions];

        if (this.enableMentions) {
          extensions.push(this.$refs.mention.plugin);
        }

        if (this.enableTags) {
          extensions.push(this.$refs.tag.plugin);
        }

        if (this.enableImages) {
          extensions.push(this.$refs.image.plugin);
        }

        if (this.enableGroups) {
          extensions.push(this.$refs.group.plugin);
        }

        const onUpdate = debounce(this.onUpdate, 350);
        this.editor = new Editor({
          extensions,
          content: this.value,
          onUpdate: onUpdate,
          onFocus: this.onFocus,
          onBlur: this.onBlur,
          editable: !this.readonly,
        });
      },

      onUpdate(content) {
        let value;

        const html = content.getHTML();

        if (this.outputType === 'html') {
          value = html;
        } else if (this.outputType === 'json') {
          value = content.getJSON();
        } else if (this.outputType === 'text') {
          value = htmlToText(html);
        } else {
          throw new Error('invalid output type');
        }

        this.$emit('input', value);
        if (this.validation) {
          this.validation.$touch();
        }
      },

      setContent() {
        if (!this.editor || !this.value || this.isContentSet) {
          return;
        }

        this.editor.setContent(this.value);
        this.isContentSet = true;
      },

      clearContent() {
        this.editor.clearContent(true);
      },

      clearContentNoEvent() {
        this.editor.clearContent();
      },

      onFocus(event) {
        this.focused = true;
        this.$emit('focus', event);
      },

      onBlur(event) {
        this.focused = false;
        this.$emit('blur', event);
      },
    },

    beforeDestroy() {
      if (this.editor) this.editor.destroy();
    },
  };
</script>

<style lang="scss" scoped>
  @import '../../sass/config/variables';
  @import '~bootstrap/scss/bootstrap';

  .is-active {
    visibility: visible;
    opacity: 1;
  }

  ::v-deep {
    .ProseMirror {
      :last-child {
        margin-bottom: 0;
      }
    }

    // input sizes
    &.form-control {
      height: auto !important;

      &.focus {
        // from bootstrap mixin form-control-focus
        outline: 0;
        box-shadow: none;

        &.is-valid {
          border-color: $form-feedback-valid-color;
          box-shadow: none;
        }

        &.is-invalid {
          border-color: $form-feedback-invalid-color;
          box-shadow: none;
        }
      }

      &.is-valid {
        background-image: none;
      }

      .ProseMirror {
        font-size: inherit;
        line-height: inherit;
        font-weight: inherit;
        color: inherit;
        overflow: auto;
      }

      .ProseMirror {
        min-height: calc(var(--min-lines) * #{$line-height-base} * #{$input-font-size});
        max-height: calc(var(--max-lines) * #{$line-height-base} * #{$input-font-size});
      }

      &.form-control-lg .ProseMirror {
        min-height: calc(var(--min-lines) * #{$line-height-base} * #{$input-font-size-lg});
        max-height: calc(var(--max-lines) * #{$line-height-base} * #{$input-font-size-lg});
      }

      &.form-control-sm .ProseMirror {
        min-height: calc(var(--min-lines) * #{$line-height-base} * #{$input-font-size-sm});
        max-height: calc(var(--max-lines) * #{$line-height-base} * #{$input-font-size-sm});
      }
    }

    .ProseMirror *.editor-empty:nth-child(1)::before,
    .ProseMirror *.editor-empty:nth-child(2)::before {
      content: attr(data-empty-text);
      float: left;
      color: var(--gray-400) !important;
      pointer-events: none;
      height: 0;
    }
  }
</style>
